"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Video, Brain, Target, Star, CheckCircle2, Volume2 } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";

export function AIInterviewShowcase() {
  const t = useTranslations("HomePage.AIInterviewShowcase");

  const showcaseRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [clarityScore, setClarityScore] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (showcaseRef.current) {
      observer.observe(showcaseRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;

    let start = 0;
    const target = 92;
    const duration = 1500;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setClarityScore(Math.floor(easeOut * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [inView]);

  return (
    <section
      ref={showcaseRef}
      className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-muted/20 to-background px-6 py-20 md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <div className="flex flex-col justify-center">
            <div className="mb-3 h-[3px] w-10 rounded-full bg-gradient-to-r from-[#FF6B3D] to-[#4B3FD1]" />

            <h2 className="mb-4 text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-4xl md:text-5xl">
              {t("title")}{" "}
              <span className="bg-gradient-to-r from-[#FF6B3D] via-[#14B8A6] to-[#4B3FD1] bg-clip-text text-transparent animate-gradient-loop">
                {t("titleHighlight")}
              </span>
            </h2>

            <p className="mb-8 text-pretty text-base text-muted-foreground sm:text-lg leading-relaxed">
              {t("description")}
            </p>

            {/* Staggered Features List */}
            <div className="mb-10 space-y-5">
              <div
                className="flex items-start gap-4 transition-all duration-700"
                style={{
                  transform: inView ? "translateY(0)" : "translateY(20px)",
                  opacity: inView ? 1 : 0,
                  transitionDelay: "100ms",
                }}
              >
                <div className="rounded-xl bg-[#FF6B3D]/10 p-3 text-[#FF6B3D] shrink-0 border border-[#FF6B3D]/20">
                  <Brain className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="mb-1 font-bold text-foreground text-base">
                    {t("features.questionGeneration.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t("features.questionGeneration.description")}
                  </p>
                </div>
              </div>

              <div
                className="flex items-start gap-4 transition-all duration-700"
                style={{
                  transform: inView ? "translateY(0)" : "translateY(20px)",
                  opacity: inView ? 1 : 0,
                  transitionDelay: "250ms",
                }}
              >
                <div className="rounded-xl bg-[#14B8A6]/10 p-3 text-[#14B8A6] shrink-0 border border-[#14B8A6]/20">
                  <Target className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="mb-1 font-bold text-foreground text-base">
                    {t("features.performanceAnalysis.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t("features.performanceAnalysis.description")}
                  </p>
                </div>
              </div>

              <div
                className="flex items-start gap-4 transition-all duration-700"
                style={{
                  transform: inView ? "translateY(0)" : "translateY(20px)",
                  opacity: inView ? 1 : 0,
                  transitionDelay: "400ms",
                }}
              >
                <div className="rounded-xl bg-[#4B3FD1]/10 p-3 text-[#4B3FD1] shrink-0 border border-[#4B3FD1]/20">
                  <Star className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="mb-1 font-bold text-foreground text-base">
                    {t("features.improvementTips.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t("features.improvementTips.description")}
                  </p>
                </div>
              </div>
            </div>

            <Link href="/login">
              <Button
                size="lg"
                className="w-fit gap-2 font-bold bg-[#FF6B3D] text-white hover:bg-[#FF6B3D]/90 shadow-lg shadow-[#FF6B3D]/20 hover:scale-105 transition-all"
              >
                <Mic className="h-5 w-5" />
                {t("cta")}
              </Button>
            </Link>
          </div>

          {/* Right Column - Animated Interactive Visual */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-2xl backdrop-blur-xl">
              <img
                src="/professional-person-in-video-interview-with-ai-int.jpg"
                alt={t("imageAlt")}
                className="h-[380px] sm:h-[420px] w-full object-cover"
              />

              {/* Floating Real-Time Audio Waveform & Status overlay */}
              <div className="absolute top-4 left-4 flex items-center gap-3 rounded-xl border border-border/80 bg-background/90 px-4 py-2.5 shadow-xl backdrop-blur-md">
                <div className="flex items-center gap-2">
                  <div className="flex h-3 items-end gap-1">
                    {[1, 2, 3, 4, 5].map((bar) => (
                      <div
                        key={bar}
                        className="w-1 bg-[#FF6B3D] rounded-full animate-waveform-bar"
                        style={{
                          animationDelay: `${bar * 150}ms`,
                          height: "100%",
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-bold text-foreground">
                    Interview in Progress
                  </span>
                </div>
                <div className="flex items-center gap-1.5 border-l border-border pl-3">
                  <span className="h-2 w-2 rounded-full bg-rose-500 animate-ping" />
                  <span className="text-[11px] font-semibold text-rose-500">
                    Recording
                  </span>
                </div>
              </div>

              {/* Success Badge Chip Pop-In */}
              <div
                className="absolute top-4 right-4 transition-all duration-700"
                style={{
                  transform: inView ? "scale(1)" : "scale(0)",
                  opacity: inView ? 1 : 0,
                  transitionDelay: "600ms",
                }}
              >
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/15 px-3.5 py-2 text-emerald-600 dark:text-emerald-400 font-bold text-xs backdrop-blur-md shadow-lg">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Great answer!</span>
                </div>
              </div>

              {/* Animated Progress Bar Overlay */}
              <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-border/80 bg-background/95 p-4 shadow-2xl backdrop-blur-md">
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Volume2 className="h-4 w-4 text-[#14B8A6]" />
                    Clarity Score
                  </span>
                  <span className="font-extrabold text-foreground text-sm">
                    {clarityScore}%
                  </span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-muted/80 p-0.5">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#FF6B3D] via-[#14B8A6] to-[#4B3FD1] transition-all duration-1000 ease-out"
                    style={{ width: `${clarityScore}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Background Glow Blobs */}
            <div className="absolute -bottom-6 -right-6 h-40 w-40 rounded-full bg-[#FF6B3D]/20 blur-3xl pointer-events-none" />
            <div className="absolute -left-6 -top-6 h-40 w-40 rounded-full bg-[#4B3FD1]/20 blur-3xl pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
}
